import Debug from "../../../../common-deprecated/Debug";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import type { PriceType } from "../../../../common-deprecated/types/CommonTypes";
import type { ExternalConfigType } from "../../../shared-logic/types/ExternalPriceType";
import { fetchAndParseJson } from "../../../../common-deprecated/utils";
import type { EquipmentV2SettingsType } from "../redux/reducers/EquipmentV2SettingsReducer";
import type {
    CCGetMonthlyPaymentResponseType,
    ParsedMonthlyRateType,
} from "../../../../common-deprecated/types/FinanceInsuranceRateTypes";
import type { SelectedCarType } from "./types";
import Navigation, { NavStep } from "../../shared-logic/utils/Navigation";
import { getAEMBuildPage } from "../../../../common-deprecated/settings/utils/commonSettingUtils";

export const fetchMonthlyRate = async (
    settings: EquipmentV2SettingsType,
    config: ExternalConfigType,
    selectedItems?: string[] | null,
    unSelectedItems?: { Id: string }[] | null,
): Promise<ParsedMonthlyRateType> => {
    const { monthlyRateConfigurablesService, monthlyRateSelectedService } = settings.finance;
    const response: any = {};
    // RequestLoader is used server side, fetchAndParseJson is used client side
    const loader = fetchAndParseJson;
    const ratePromises: (CCGetMonthlyPaymentResponseType | null)[] = [];

    // Get rates for equipment if necessary.
    const accRateConfig: any = {
        Brand: config.Brand,
        Country: config.Country,
        CarId: config.CarID,
        ModelId: config.ModelID,
        ExteriorColourId: config.ExteriorColourID,
        UpholsteryId: config.UpholsteryID,
        SelectedItems: selectedItems,
        UnselectedItems: unSelectedItems,
    };
    // Also inherit the personalized quote id parameters when relevant
    if (config.Parameters) {
        accRateConfig.Parameters = { ...config.Parameters };
    }
    // Also inherit PSLS information when applicable
    if (config.PSLS) {
        accRateConfig.PSLS = config.PSLS;
    }

    ratePromises.push(
        monthlyRateConfigurablesService
            ? loader(monthlyRateConfigurablesService, "unselected items rate", accRateConfig)
            : null,
    );

    ratePromises.push(
        monthlyRateSelectedService ? loader(monthlyRateSelectedService, "selected items rate", accRateConfig) : null,
    );

    // Execute requests to monthly rate endpoints.
    const [equipmentMonthlyRates, selectedEquipmentRates] = await Promise.all(ratePromises);

    const processItemRates = (ratesObject: any, ratesObjectItemProperty: string): any[] => {
        if (ratesObject) {
            if (ratesObject.Error || !ratesObject.Quotation) {
                Debug.error(ratesObject.Error, "No finance quotes provided");
            } else {
                // Get item prices from equipmentMonthlyRates response.
                return ratesObject.Quotation[ratesObjectItemProperty].map((item: any) => ({
                    id: item.Id,
                    value: item.Value,
                }));
            }
        }
        return [];
    };
    response.unselectedItemPrices = equipmentMonthlyRates
        ? processItemRates(equipmentMonthlyRates, "UnselectedItems")
        : [];
    response.selectedItemPrices = selectedEquipmentRates
        ? processItemRates(selectedEquipmentRates, "SelectedItems")
        : [];

    return response;
};

// Helper method to determin if a price or monthly rate should be shown
export const showEquipmentPrice = (price: PriceType): boolean => {
    return price.discount > 0 && price.cash > 0;
};

export const getBuildUrl = (selectedCar: SelectedCarType, commonSettings: CommonSettingsType): string => {
    if (commonSettings.isStandalone) {
        const navBuildPath = Navigation.build(
            commonSettings,
            NavStep.Customize,
            {
                modelId: selectedCar.modelId,
                carId: selectedCar.carId,
                gradeId: "", // not used on customize steps
                bodyTypeId: "",
            },
            selectedCar.colourId ? `c=${selectedCar.colourId}` : undefined,
        );

        return `/${commonSettings.country}/${commonSettings.language}/build-and-buy${navBuildPath}`;
    }

    return getAEMBuildPage(commonSettings, selectedCar.modelId, selectedCar.colourId || "", selectedCar.carId);
};
